import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Slider,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { SparaVerticalCard, SparaHorizontalCard } from 'components/SparaOfferCard/SparaOfferCard';
import styles from './ImageCropModal.module.scss';

function parseAspect(aspectStr) {
    if (!aspectStr) return 3 / 2;
    if (typeof aspectStr === 'number') return aspectStr;
    const parts = String(aspectStr).split('/');
    if (parts.length === 2) return parseFloat(parts[0]) / parseFloat(parts[1]);
    return parseFloat(aspectStr) || 3 / 2;
}

function createImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', () => reject(new Error('Failed to load image')));
        img.src = url;
    });
}

async function getCroppedBase64(imageSrc, pixelCrop) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );
    return canvas.toDataURL('image/jpeg', 0.92);
}

// When fill is enabled and zoomed out, croppedAreaPixels can have negative x/y.
async function getFilledFromCrop(imageSrc, cropPixels, aspect, fillColor) {
    const image = await createImage(imageSrc);
    const imgW = image.naturalWidth;
    const imgH = image.naturalHeight;

    const canvasW = 900;
    const canvasH = Math.round(canvasW / aspect);

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = fillColor;
    ctx.fillRect(0, 0, canvasW, canvasH);

    const scaleX = canvasW / cropPixels.width;
    const scaleY = canvasH / cropPixels.height;
    const destX = -cropPixels.x * scaleX;
    const destY = -cropPixels.y * scaleY;
    const destW = imgW * scaleX;
    const destH = imgH * scaleY;

    ctx.drawImage(image, 0, 0, imgW, imgH, destX, destY, destW, destH);

    return canvas.toDataURL('image/jpeg', 0.92);
}

async function getSquareCenterCrop(base64) {
    const image = await createImage(base64);
    const size = Math.min(image.naturalWidth, image.naturalHeight);
    const sx = (image.naturalWidth - size) / 2;
    const sy = (image.naturalHeight - size) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.getContext('2d').drawImage(image, sx, sy, size, size, 0, 0, size, size);
    return canvas.toDataURL('image/jpeg', 0.92);
}

export default function ImageCropModal({ open, imageSrc, aspectStr, offer, onConfirm, onCancel, cropShape = 'rect' }) {
    const aspect = parseAspect(aspectStr);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [fillEnabled, setFillEnabled] = useState(false);
    const [fillColor, setFillColor] = useState('#ffffff');
    const [processing, setProcessing] = useState(false);
    const [showSafeZone, setShowSafeZone] = useState(true);
    const [previewBase64, setPreviewBase64] = useState(null);
    const [previewSquare, setPreviewSquare] = useState(null);
    const previewTimerRef = useRef(null);

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            setFillEnabled(false);
            setFillColor('#ffffff');
            setPreviewBase64(null);
            setPreviewSquare(null);
        }
    }, [open]);

    const handleFillToggle = async (e) => {
        const enabled = e.target.checked;
        setFillEnabled(enabled);
        setCrop({ x: 0, y: 0 });
        setCroppedAreaPixels(null);
        setPreviewBase64(null);
        setPreviewSquare(null);

        if (enabled && imageSrc) {
            try {
                const img = await createImage(imageSrc);
                const imgAspect = img.naturalWidth / img.naturalHeight;
                // Calculate zoom to fit entire image inside the 1:1 safe zone box.
                // At zoom=1 the image covers the 3:2 crop area.
                // The 1:1 box = cropH × cropH within the cropW(=cropH*aspect) × cropH area.
                let fitZoom;
                if (imgAspect >= aspect) {
                    fitZoom = 1 / imgAspect;
                } else if (imgAspect >= 1) {
                    fitZoom = 1 / aspect;
                } else {
                    fitZoom = imgAspect / aspect;
                }
                setZoom(Math.max(0.05, fitZoom * 0.9));
            } catch {
                setZoom(0.5);
            }
        } else {
            setZoom(1);
        }
    };

    useEffect(() => {
        if (!imageSrc) return;
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = setTimeout(() => {
            generatePreview();
        }, 300);
        return () => clearTimeout(previewTimerRef.current);
    }, [croppedAreaPixels, fillColor, fillEnabled, imageSrc]);

    const generatePreview = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        try {
            let base64;
            if (fillEnabled) {
                base64 = await getFilledFromCrop(imageSrc, croppedAreaPixels, aspect, fillColor);
            } else {
                base64 = await getCroppedBase64(imageSrc, croppedAreaPixels);
            }
            if (!base64) return;
            const square = await getSquareCenterCrop(base64);
            setPreviewBase64(base64);
            setPreviewSquare(square);
        } catch (err) {
            console.warn('Preview generation failed:', err?.message || err);
        }
    };

    const handleConfirm = async () => {
        if (!croppedAreaPixels) return;
        setProcessing(true);
        try {
            let result;
            if (fillEnabled) {
                result = await getFilledFromCrop(imageSrc, croppedAreaPixels, aspect, fillColor);
            } else {
                result = await getCroppedBase64(imageSrc, croppedAreaPixels);
            }
            onConfirm(result);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
        >
            <DialogTitle className={styles.dialogTitle} style={{ padding: '12px 16px' }}>
                <div className={styles.titleRow}>
                    <span className={styles.titleText}>Breyta mynd</span>
                    <IconButton
                        size="small"
                        onClick={onCancel}
                        className={styles.closeBtn}
                        style={{ marginLeft: 'auto' }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent style={{ padding: '12px 20px 0' }}>
                <div
                    className={`${fillEnabled ? styles.fillCropArea : styles.cropArea} ${
                        showSafeZone && cropShape !== 'round' ? styles.cropAreaWithSafeZone : ''
                    }`}
                    style={{
                        ...(cropShape === 'round'
                            ? { width: 'min(100%, 420px)', aspectRatio: '1/1', margin: '0 auto' }
                            : { width: '100%', height: 370 }),
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 8,
                        ...(fillEnabled ? { '--fill-color': fillColor } : {}),
                    }}
                >
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            cropShape={cropShape}
                            objectFit="cover"
                            zoomWithScroll={false}
                            showGrid={false}
                            restrictPosition={!fillEnabled && cropShape !== 'round'}
                            minZoom={fillEnabled ? 0.05 : cropShape === 'round' ? 0.3 : 1}
                            maxZoom={3}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            style={
                                fillEnabled
                                    ? { containerStyle: { background: fillColor } }
                                    : undefined
                            }
                        />
                    )}
                </div>
                <div className={styles.zoomRow}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={fillEnabled}
                                onChange={handleFillToggle}
                                size="small"
                                sx={{
                                    color: 'var(--spara-text-muted, #888)',
                                    '&.Mui-checked': {
                                        color: 'var(--spara-primary, #de2c48)',
                                    },
                                    p: '4px',
                                }}
                            />
                        }
                        label="Fylla með lit"
                        className={styles.fillCheckLabel}
                    />
                    {fillEnabled && (
                        <div className={styles.fillControls}>
                            <div className={styles.colorPickerWrap}>
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) => setFillColor(e.target.value)}
                                    className={styles.colorInput}
                                />
                                <input
                                    type="text"
                                    value={fillColor.toUpperCase()}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setFillColor(v);
                                        if (/^#[0-9a-fA-F]{6}$/.test(v)) setFillColor(v);
                                    }}
                                    className={styles.colorHexInput}
                                    maxLength={7}
                                    spellCheck={false}
                                />
                            </div>
                            <div className={styles.colorPresets}>
                                {[
                                    '#ffffff',
                                    '#000000',
                                    '#f5f5f5',
                                    '#1a1a2e',
                                    '#de2c48',
                                    '#2e7d32',
                                ].map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`${styles.colorPreset} ${
                                            fillColor === c ? styles.colorPresetActive : ''
                                        }`}
                                        style={{
                                            background: c,
                                            border: c === '#ffffff' ? '1px solid #ddd' : 'none',
                                        }}
                                        onClick={() => setFillColor(c)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <span className={styles.zoomLabel}>Stækka</span>
                    <Slider
                        value={zoom}
                        min={fillEnabled ? 0.05 : cropShape === 'round' ? 0.3 : 1}
                        max={3}
                        step={0.05}
                        onChange={(_, v) => setZoom(v)}
                        sx={{ color: 'var(--spara-primary, #de2c48)', flex: 1, mx: 2 }}
                    />
                </div>

                {/* Live preview panel */}
                <div className={styles.previewPanel}>
                    <p className={styles.previewTitle}>Forskoðun</p>
                    {cropShape === 'round' ? (
                        <div className={styles.logoPreviewRow}>
                            {previewBase64 && (
                                <img src={previewBase64} className={styles.logoPreviewCircle} alt="" />
                            )}
                        </div>
                    ) : (
                        <div className={styles.previewRow}>
                            <div className={styles.previewCardWrap}>
                                <span className={styles.previewCardLabel}>Lóðrétt kort</span>
                                <SparaVerticalCard offer={{ ...offer, image: previewBase64 }} />
                            </div>
                            <div className={styles.previewCardWrapWide}>
                                <span className={styles.previewCardLabel}>Lárétt kort</span>
                                <SparaHorizontalCard offer={{ ...offer, image: previewSquare }} />
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>

            <DialogActions
                style={{
                    padding: '12px 16px',
                    borderTop: '1px solid var(--spara-border, #f0f0f0)',
                    gap: 8,
                }}
            >
                <button type="button" className={styles.cancelBtn} onClick={onCancel}>
                    Hætta við
                </button>
                <button
                    type="button"
                    className={styles.confirmBtn}
                    onClick={handleConfirm}
                    disabled={processing}
                >
                    <Check style={{ fontSize: 18 }} />
                    {processing ? 'Hleð...' : 'Staðfesta'}
                </button>
            </DialogActions>
        </Dialog>
    );
}
